import { createSelector } from 'reselect'
import { State } from '../../../store.types'
import { createDeepEqualSelector } from '../../../store'
import { UserEnvironmentWithZonesHotelsAndRooms } from '../ApiUserDetailsSlice/ApiUserDetailsSlice.types'
import { HotelAuxListWithRoomTypes } from '../ApiUserDetailsSlice/ApiUserDetailsSlice.selectors'

interface SeparatedHotelAuxList {
  hotelAuxListWithZones: HotelAuxListWithRoomTypes[]
  hotelAuxListWithoutZones: HotelAuxListWithRoomTypes[]
}

export const selectApiUsers = (state: State) => state.apiUsers

export const selectApiUsersState = (windowId: string) =>
  createSelector(selectApiUsers, (apiUsersState) =>
    apiUsersState.ApiUsersStates.find(
      (apiUser) => apiUser.windowId === windowId
    )
  )

export const selectUserAux = (windowId: string) =>
  createDeepEqualSelector(
    selectApiUsersState(windowId),
    (apiUsersState) => apiUsersState?.userAuxList
  )

export const selectApiUsersLoading = (windowId: string) =>
  createDeepEqualSelector(
    selectApiUsersState(windowId),
    (apiUserState) => apiUserState?.loading
  )

export const selectFormattedUserAux = (windowId: string) =>
  createDeepEqualSelector(selectApiUsersState(windowId), (apiUsers) =>
    apiUsers?.userAuxList.map((user) => {
      const userRoleIds = apiUsers.userRoleAuxList.filter(
        (userRoleAux) => userRoleAux.userId === user.id
      )
      let userRoles
      if (userRoleIds.length > 0) {
        userRoles = apiUsers.roleAuxList.filter((roleAux) =>
          userRoleIds.some((userRole) => userRole.roleId === roleAux.id)
        )
      }
      return {
        id: user.id,
        firstname: user.firstName,
        lastname: user.lastName,
        email: user.name,
        lastConnection: user.lastConnection,
        status: user.active,
        creationDate: user.creationDate,
        roles: userRoles,
        token: user.token
      }
    })
  )
export const selectOneFormattedUserAux = (windowId: string, userId: string) =>
  createDeepEqualSelector(selectApiUsersState(windowId), (apiUsers) => {
    const user = apiUsers?.userAuxList.find((userAux) => userAux.id === userId)
    if (user == null) return
    const userRoleIds = apiUsers!.userRoleAuxList.filter(
      (userRoleAux) => userRoleAux.userId === user.id
    )
    let userRoles
    if (userRoleIds.length > 0) {
      userRoles = apiUsers!.roleAuxList.filter((roleAux) =>
        userRoleIds.some((userRole) => userRole.roleId === roleAux.id)
      )
    }
    return {
      id: user.id,
      firstname: user.firstName,
      lastname: user.lastName,
      email: user.name,
      lastConnection: user.lastConnection,
      status: user.active,
      creationDate: user.creationDate,
      roles: userRoles
    }
  })

export const selectUserRole = ({
  windowId,
  userId
}: {
  windowId: string
  userId: string
}) =>
  createDeepEqualSelector(selectApiUsersState(windowId), (apiUserState) => {
    const userRoleId = apiUserState?.userRoleAuxList.find(
      (userRoleAux) => userRoleAux.userId === userId
    )?.roleId
    let userRole
    if (userRoleId) {
      userRole = apiUserState.roleAuxList.find(
        (role) => role.id === userRoleId
      )
    }
    return userRole
  })

export const selectRoleAuxList = (windowId: string) =>
  createDeepEqualSelector(
    selectApiUsersState(windowId),
    (apiUserState) => apiUserState?.roleAuxList
  )
export const selectUserRoleAuxList = (windowId: string) =>
  createDeepEqualSelector(
    selectApiUsersState(windowId),
    (apiUserState) => apiUserState?.userRoleAuxList
  )

export const selectApiUserEnvironmentDataAuxList = (windowId: string) =>
  createDeepEqualSelector(
    [selectApiUsersState(windowId)],
    (apiUserState) => apiUserState?.environmentDataAuxList
  )

export const selectApiUserZoneAuxList = (windowId: string) =>
  createDeepEqualSelector(
    [selectApiUsersState(windowId)],
    (apiUserState) => apiUserState?.zoneAuxList
  )
export const selectApiUserHotelAuxList = (windowId: string) =>
  createDeepEqualSelector(
    [selectApiUsersState(windowId)],
    (apiUserState) => apiUserState?.hotelAuxList
  )

export const selectApiUserRoomTypesAuxList = (windowId: string) =>
  createDeepEqualSelector(
    [selectApiUsersState(windowId)],
    (apiUserState) => apiUserState?.roomTypeAuxList
  )

export const selectApiUserRoomAuxList = (windowId: string) =>
  createDeepEqualSelector(
    [selectApiUsersState(windowId)],
    (apiUserState) => apiUserState?.roomAuxList
  )

export const selectApiUserChannelAuxList = (windowId: string) =>
  createDeepEqualSelector(
    [selectApiUsersState(windowId)],
    (apiUserState) => apiUserState?.channelAuxList
  )

export const selectApiUserBillingEntityAuxList = (windowId: string) =>
  createDeepEqualSelector(
    [selectApiUsersState(windowId)],
    (apiUserState) => apiUserState?.billingEntityAuxList
  )

export const selectApiUserLanguageAuxList = (windowId: string) =>
  createDeepEqualSelector(
    [selectApiUsersState(windowId)],
    (apiUserState) => apiUserState?.languageAuxList
  )

export const selectApiUserEnvironmentDataAuxListWithRoles = (
  windowId: string
) =>
  createSelector(
    [
      selectApiUserEnvironmentDataAuxList(windowId),
      selectRoleAuxList(windowId)
    ],
    (environmentDataAuxList, roleAuxList) =>
      environmentDataAuxList?.map((environmentDataAux) => {
        const environmentRoles = roleAuxList?.filter(
          (role) => role.environmentDataId === environmentDataAux.id
        )
        return {
          ...environmentDataAux,
          roles: environmentRoles
        }
      })
  )

export const selectApiUserEnvironmentDataAuxListWithChannels = (
  windowId: string
) =>
  createSelector(
    [
      selectApiUserEnvironmentDataAuxList(windowId),
      selectApiUserChannelAuxList(windowId)
    ],
    (userEnvironments, userChannelAuxList) => {
      return userEnvironments?.map((environment) => {
        const channels = userChannelAuxList?.filter(
          (channel) => channel.environmentDataId === environment.id
        )

        return {
          ...environment, channels
        }
      })
    }
  )

export const selectApiUserEnvironmentDataAuxListWithBillingEntity = (
  windowId: string
) =>
  createSelector(
    [
      selectApiUserEnvironmentDataAuxList(windowId),
      selectApiUserBillingEntityAuxList(windowId)
    ],
    (userEnvironments, userBillingEntityAuxList) =>
      userEnvironments?.map((environment) => {
        const environmentBillingEntityAuxList =
          userBillingEntityAuxList?.filter(
            (billingEntity) =>
              billingEntity.environmentDataId === environment.id
          )
        return {
          ...environment,
          billingEntities: environmentBillingEntityAuxList
        }
      })
  )

export const selectUserAccomodations = (windowId: string) =>
  createSelector(
    [
      selectApiUserEnvironmentDataAuxList(windowId),
      selectApiUserZoneAuxList(windowId),
      selectApiUserHotelAuxList(windowId),
      selectApiUserRoomTypesAuxList(windowId),
      selectApiUserRoomAuxList(windowId)
    ],
    (
      userEnvironments,
      userZoneAuxList,
      userHotelAuxList,
      userRoomTypesAuxList,
      userRoomAuxList
    ): UserEnvironmentWithZonesHotelsAndRooms[] => {
      const { hotelAuxListWithZones, hotelAuxListWithoutZones } =
        userHotelAuxList?.reduce(
          (acum, hotel) => {
            const hotelWithRoomTypes = {
              ...hotel,
              roomTypes:
                userRoomTypesAuxList
                  ?.filter((roomType) => roomType.hotelId === hotel.id)
                  .map((roomType) => {
                    return {
                      ...roomType,
                      rooms:
                        userRoomAuxList?.filter(
                          (room) => room.roomTypeId === roomType.id
                        ) ?? []
                    }
                  }) ?? []
            }
            if (hotelWithRoomTypes.zoneId) {
              return {
                ...acum,
                hotelAuxListWithZones: [
                  ...acum.hotelAuxListWithZones,
                  hotelWithRoomTypes
                ]
              }
            }
            return {
              ...acum,
              hotelAuxListWithoutZones: [
                ...acum.hotelAuxListWithoutZones,
                hotelWithRoomTypes
              ]
            }
          },
          {
            hotelAuxListWithZones: [],
            hotelAuxListWithoutZones: []
          } as SeparatedHotelAuxList
        ) ?? {
          hotelAuxListWithZones: [],
          hotelAuxListWithoutZones: []
        }

      return (
        userEnvironments?.map((environment) => {
          const environmentZones =
            userZoneAuxList
              ?.filter(
                (zoneAux) => zoneAux.environmentDataId === environment.id
              )
              .map((zone) => {
                return {
                  ...zone,
                  hotels: (
                    hotelAuxListWithZones
                  )?.filter((hotel) => hotel.zoneId! === zone.id)
                }
              }) ?? []
          return {
            ...environment,
            zones: environmentZones,
            hotels: (
              hotelAuxListWithoutZones
            ).filter((hotel) => hotel.environmentDataId === environment.id)
          }
        }) ?? []
      )
    }
  )
